import { z } from 'zod';

/**
 * Lebenszyklus eines Worker-Nodes aus Sicht des Masters.
 *
 * Der Status wird ausschließlich vom Master gesetzt, niemals vom Worker
 * gemeldet — ein Node, der sich selbst für gesund erklärt, während er nicht
 * erreichbar ist, wäre wertlos. Grundlage ist allein das Alter des letzten
 * Heartbeats plus die administrative Absicht (`drain`, `maintenance`).
 */
export const NODE_STATUS = {
  /** Angelegt, Bootstrap-Token ausgegeben, aber noch nie erfolgreich registriert. */
  enrolling: 'enrolling',
  /** Heartbeat frisch. Einziger Status, in dem der Scheduler platzieren darf. */
  online: 'online',
  /** Heartbeat überfällig, aber noch nicht als tot gewertet. Keine neuen Sites. */
  degraded: 'degraded',
  /** Heartbeat lange überfällig. Node gilt als ausgefallen. */
  offline: 'offline',
  /** Administrativ aus der Platzierung genommen; laufende Sites bleiben aktiv. */
  draining: 'draining',
  /** Geplante Wartung. Wie `draining`, aber ohne Migrationsabsicht. */
  maintenance: 'maintenance',
} as const;

export type NodeStatus = (typeof NODE_STATUS)[keyof typeof NODE_STATUS];

export const nodeStatusSchema = z.enum([
  NODE_STATUS.enrolling,
  NODE_STATUS.online,
  NODE_STATUS.degraded,
  NODE_STATUS.offline,
  NODE_STATUS.draining,
  NODE_STATUS.maintenance,
]);

/**
 * Zustände, in denen der Scheduler einem Node neue Sites zuweisen darf.
 *
 * Bewusst als explizite Liste statt als Negation ("alles außer offline"):
 * Ein neu hinzugefügter Status ist damit automatisch nicht platzierbar, bis
 * jemand ihn bewusst freischaltet. Sicherheit durch Vorgabewert.
 */
export const SCHEDULABLE_NODE_STATUSES: readonly NodeStatus[] = [NODE_STATUS.online];

/**
 * Prüft, ob ein Status grundsätzlich Platzierungen erlaubt.
 *
 * Achtung: Dies ist nur eine von mehreren Bedingungen. Der Scheduler prüft
 * zusätzlich das Flag `schedulable` und die freie Kapazität.
 */
export function isSchedulableStatus(status: NodeStatus): boolean {
  return SCHEDULABLE_NODE_STATUSES.includes(status);
}

/**
 * Zustände, die als "Node ist erreichbar" gelten.
 * Für `draining` und `maintenance` gilt das ebenfalls — sie sind administrative
 * Einschränkungen, keine Ausfälle.
 */
export function isReachableStatus(status: NodeStatus): boolean {
  return (
    status === NODE_STATUS.online ||
    status === NODE_STATUS.degraded ||
    status === NODE_STATUS.draining ||
    status === NODE_STATUS.maintenance
  );
}
